import React, { useEffect, useMemo, useRef, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import apiService from "../services/api.service";
import { formatRomeDateTime } from "../utils/datetime";

const recipientName = (user) => `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username;

const EventInvitationModal = ({ users, initialEventId = "", onHide }) => {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [sentIds, setSentIds] = useState(new Set());
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState({ processed: 0, total: 0 });
  const sendingRef = useRef(false);

  useEffect(() => {
    let active = true;
    apiService.getEvents().then(({ data }) => {
      if (!active) return;
      const available = data || [];
      setEvents(available);
      if (available.some((event) => String(event.id) === String(initialEventId) && !event.finished)) {
        setEventId(String(initialEventId));
      }
    }).catch(() => {
      if (active) setError("Impossibile caricare gli eventi.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [initialEventId]);

  const event = events.find((item) => String(item.id) === eventId && !item.finished);
  const roster = useMemo(() => users.filter((user) =>
    (user.event_registrations || []).some((registration) => String(registration.event_id) === eventId)
  ), [users, eventId]);

  useEffect(() => {
    setSelected(new Set(roster.map((user) => String(user.id))));
    setSentIds(new Set());
    setResult(null);
    setProgress({ processed: 0, total: 0 });
  }, [roster]);

  const eligible = roster.filter((user) => !sentIds.has(String(user.id)));
  const allSelected = eligible.length > 0 && eligible.every((user) => selected.has(String(user.id)));
  const selectedIds = roster.filter((user) => selected.has(String(user.id)) && !sentIds.has(String(user.id)))
    .map((user) => Number(user.id));

  const toggleUser = (id) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(String(id))) next.delete(String(id));
      else next.add(String(id));
      return next;
    });
  };

  const send = async () => {
    if (sendingRef.current || !event || !selectedIds.length) return;
    sendingRef.current = true;
    setSending(true);
    setError("");
    setResult(null);
    setProgress({ processed: 0, total: selectedIds.length });
    const accepted = new Set();
    const failed = new Set();
    try {
      for (let offset = 0; offset < selectedIds.length; offset += 50) {
        const batch = selectedIds.slice(offset, offset + 50);
        const { data } = await apiService.sendEventInvitations(event.id, batch);
        if (!Array.isArray(data?.sent) || !Array.isArray(data?.failed)) throw new Error("Invalid send result");
        const batchIds = new Set(batch.map(String));
        const reported = [...data.sent, ...data.failed].map(String);
        if (reported.length !== batch.length || new Set(reported).size !== batch.length ||
            reported.some((id) => !batchIds.has(id))) throw new Error("Incomplete send result");
        data.sent.forEach((id) => accepted.add(String(id)));
        data.failed.forEach((id) => failed.add(String(id)));
        setSentIds((current) => new Set([...current, ...data.sent.map(String)]));
        setSelected(new Set(selectedIds.map(String).filter((id) => !accepted.has(id))));
        setProgress({ processed: offset + batch.length, total: selectedIds.length });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError("Alcuni invii non sono confermati. I destinatari non confermati e quelli non ancora elaborati restano selezionati. " +
        (typeof detail === "string" ? detail : "Riprovando, chi ha già ricevuto un invio non confermato potrebbe ricevere una seconda email."));
    } finally {
      setSelected(new Set(selectedIds.map(String).filter((id) => !accepted.has(id))));
      setResult({ sent: accepted.size, failed: failed.size,
        unconfirmed: selectedIds.length - accepted.size - failed.size });
      sendingRef.current = false;
      setSending(false);
    }
  };

  return (
    <Modal show onHide={() => { if (!sendingRef.current) onHide(); }} centered size="lg"
      backdrop={sending ? "static" : true} keyboard={!sending} aria-labelledby="event-invitations-title">
      <Modal.Header closeButton={!sending}>
        <Modal.Title id="event-invitations-title">Invia mail agli iscritti</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Scegli l'evento e i destinatari. Ogni email contiene un link personale per accedere all'evento senza password.</p>
        {error && <Alert variant="danger" role="alert">{error}</Alert>}
        {sending && <p role="status">Invio: {progress.processed} di {progress.total} destinatari elaborati.</p>}
        {result && <Alert variant={result.failed || result.unconfirmed ? "warning" : "success"} role="status">
          Email accettate dal servizio: {result.sent}. Invii non riusciti: {result.failed}.
          {result.failed > 0 && <div>I destinatari non riusciti restano selezionati per riprovare l'invio.</div>}
          {result.unconfirmed > 0 && <div>Invii non confermati o non elaborati: {result.unconfirmed}.</div>}
        </Alert>}
        {loading ? <p role="status">Caricamento eventi...</p> : (
          <Form.Group className="mb-3" controlId="invitation-event">
            <Form.Label>Evento per l'invio</Form.Label>
            <Form.Select value={eventId} disabled={sending} onChange={(e) => { setEventId(e.target.value); setError(""); }}>
              <option value="">Seleziona un evento</option>
              {events.map((item) => <option key={item.id} value={item.id} disabled={item.finished}>
                {item.name} — {formatRomeDateTime(item.date)}{item.finished ? " (terminato)" : ""}
              </option>)}
            </Form.Select>
          </Form.Group>
        )}
        {event && <>
          <p>{roster.length} iscritti all'evento · {selectedIds.length} destinatari selezionati</p>
          {roster.length === 0 ? <Alert variant="info">Nessun iscritto a questo evento.</Alert> : <>
            <Form.Check className="mb-3" type="checkbox" id="invitation-select-all"
              label="Seleziona tutti gli iscritti" checked={allSelected} disabled={sending || !eligible.length}
              aria-checked={selectedIds.length > 0 && !allSelected ? "mixed" : allSelected}
              onChange={() => setSelected(new Set(allSelected ? [] : eligible.map((user) => String(user.id))))} />
            <div className="table-responsive" style={{ maxHeight: "45vh", overflowY: "auto" }}>
              <table className="table table-sm align-middle">
                <thead><tr><th scope="col">Seleziona</th><th scope="col">Nome</th><th scope="col">Email</th></tr></thead>
                <tbody>{roster.map((user) => <tr key={user.id}>
                  <td><Form.Check type="checkbox" id={`invitation-recipient-${user.id}`}
                    aria-label={`Seleziona ${recipientName(user)} (${user.username})`}
                    checked={selected.has(String(user.id))} disabled={sending || sentIds.has(String(user.id))}
                    onChange={() => toggleUser(user.id)} /></td>
                  <td>{recipientName(user)}{sentIds.has(String(user.id)) && <small className="d-block text-success">Invio accettato</small>}</td>
                  <td style={{ overflowWrap: "anywhere" }}>{user.username}</td>
                </tr>)}</tbody>
              </table>
            </div>
          </>}
        </>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={onHide} disabled={sending}>Chiudi</Button>
        <Button className="users-edit-btn" onClick={send}
          disabled={loading || sending || !event || !selectedIds.length}>
          {sending ? "Invio in corso..." : "Invia ora"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EventInvitationModal;
